import {
  ArrowRight,
  CheckCircle2,
  Download,
  Files,
  ImageIcon,
  Sparkles,
  Video,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import type {
  PublicShareMetadata,
  ShareTheme,
  UnlockShareResponse,
} from "@xiaotian-vault/shared";
import { DEFAULT_SHARE_THEME } from "@xiaotian-vault/shared";
import { BrandMark } from "../components/BrandMark.js";
import { DynamicAmbientBackdrop } from "../components/DynamicAmbientBackdrop.js";
import { LocaleToggle } from "../components/LocaleToggle.js";
import { PasskeyInput } from "../components/PasskeyInput.js";
import { ApiClientError, createApiClient } from "../lib/api.js";
import {
  DEMO_PASSKEY,
  demoPublicMetadata,
  demoUnlockResponse,
  isDemoExpiredToken,
  isDemoShareToken,
} from "../lib/demo.js";
import { formatBytes, formatDate } from "../lib/format.js";
import { useI18n, type TranslationKey } from "../lib/i18n.js";

const api = createApiClient();

export function PublicSharePage({ token }: { token: string }) {
  const { locale, t } = useI18n();
  const [metadata, setMetadata] = useState<PublicShareMetadata | null>(null);
  const [unlocked, setUnlocked] = useState<UnlockShareResponse | null>(null);
  const [passkey, setPasskey] = useState("");
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    if (isDemoShareToken(token)) {
      setMetadata(demoPublicMetadata());
      setErrorKey(null);
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    if (isDemoExpiredToken(token)) {
      setMetadata(null);
      setErrorKey("expired");
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    api
      .publicMetadata(token)
      .then((result) => {
        if (alive) {
          setMetadata(result);
          setErrorKey(null);
        }
      })
      .catch((err) => {
        if (alive) setErrorKey(errorKeyFor(err));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token, t]);

  const canSubmit = passkey.length === 4 && !unlocking;
  const expires = useMemo(
    () => formatDate(metadata?.expiresAt ?? null, locale),
    [metadata?.expiresAt, locale],
  );
  const isSealed = !loading && !metadata && Boolean(errorKey);
  const activeTheme = metadata?.theme ?? DEFAULT_SHARE_THEME;
  const unlockedPreview =
    unlocked?.previewUrl && unlocked.previewKind
      ? {
          kind: unlocked.previewKind,
          url: unlocked.previewUrl,
        }
      : null;
  const artifactIcon = !unlocked ? (
    <Files size={46} />
  ) : unlockedPreview?.kind === "image" ? (
    <ImageIcon size={46} />
  ) : unlockedPreview?.kind === "video" ? (
    <Video size={46} />
  ) : (
    <Download size={46} />
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setUnlocking(true);
    setErrorKey(null);
    try {
      if (isDemoShareToken(token)) {
        await new Promise((resolve) => window.setTimeout(resolve, 320));
        if (passkey !== DEMO_PASSKEY) {
          setErrorKey("wrongPasskey");
          setPasskey("");
          return;
        }
        setUnlocked(demoUnlockResponse());
        return;
      }

      const result = await api.unlockShare(token, passkey);
      setUnlocked(result);
    } catch (err) {
      setErrorKey(errorKeyFor(err));
      setPasskey("");
    } finally {
      setUnlocking(false);
    }
  }

  return (
    <main
      className={`public-vault theme-${activeTheme} ${unlocked ? "is-unlocked" : ""}`}
    >
      <div className="vault-overlay" />
      <DynamicAmbientBackdrop variant="public" theme={activeTheme} />
      <PublicThemeScene
        codeProgress={passkey.length}
        theme={activeTheme}
        unlocked={Boolean(unlocked)}
        verifying={unlocking}
      />
      <header className="topbar public">
        <BrandMark />
        <LocaleToggle />
      </header>

      <section className="unlock-stage">
        <div className="file-artifact" aria-hidden="true">
          <div className="artifact-ring one" />
          <div className="artifact-ring two" />
          <div className="artifact-ring three" />
          <div className="artifact-core">{artifactIcon}</div>
          <span className="artifact-burst" />
        </div>

        <form
          className={`unlock-panel ${isSealed ? "is-sealed" : ""}`}
          onSubmit={submit}
        >
          <span className="eyebrow">
            <Sparkles size={15} />
            {t("brand")}
          </span>
          <h1>
            {isSealed
              ? t("sealedTitle")
              : unlocked
                ? t("ready")
                : t("publicTitle")}
          </h1>
          <p>
            {isSealed
              ? t("sealedSubtitle")
              : unlocked
                ? t("grantHint")
                : t("publicSubtitle")}
          </p>

          {loading && <div className="status-line">{t("loading")}</div>}
          {errorKey && <div className="status-line error">{t(errorKey)}</div>}

          {metadata && !unlocked && (
            <>
              <div className="file-summary">
                <Files size={22} />
                <span>
                  <strong>{metadata.fileName}</strong>
                  <small>
                    {formatBytes(metadata.sizeBytes)} ·{" "}
                    {metadata.mimeType || "application/octet-stream"}
                    {expires ? ` · ${t("expires")} ${expires}` : ""}
                  </small>
                </span>
              </div>
              <PasskeyInput
                value={passkey}
                onChange={setPasskey}
                disabled={unlocking}
                label={t("enterPasskey")}
              />
              <button
                className="primary-button wide"
                type="submit"
                disabled={!canSubmit}
              >
                <ArrowRight size={18} />
                {unlocking ? t("unlocking") : t("unlock")}
              </button>
            </>
          )}

          {unlocked && (
            <div className="download-ready">
              <div className="trust-strip">
                <span>
                  <CheckCircle2 size={15} />
                  {t("codeAccepted")}
                </span>
                <span>
                  <Sparkles size={15} />
                  {t("readyToDownload")}
                </span>
              </div>
              <div className="file-summary bright">
                <Files size={24} />
                <span>
                  <strong>{unlocked.fileName}</strong>
                  <small>
                    {formatBytes(unlocked.sizeBytes)} ·{" "}
                    {unlocked.mimeType || "application/octet-stream"}
                  </small>
                </span>
              </div>
              {unlockedPreview && (
                <div className={`media-preview is-${unlockedPreview.kind}`}>
                  <div className="media-preview-toolbar">
                    <span>
                      {unlockedPreview.kind === "image" ? (
                        <ImageIcon size={15} />
                      ) : (
                        <Video size={15} />
                      )}
                      {t("preview")}
                    </span>
                  </div>
                  <div className="media-preview-frame">
                    {unlockedPreview.kind === "image" ? (
                      <img
                        src={unlockedPreview.url}
                        alt={unlocked.fileName}
                        decoding="async"
                      />
                    ) : (
                      <video
                        src={unlockedPreview.url}
                        controls
                        playsInline
                        preload="metadata"
                        aria-label={unlocked.fileName}
                      />
                    )}
                  </div>
                </div>
              )}
              <a
                className="primary-button wide download-button"
                href={unlocked.downloadUrl}
                download={unlocked.fileName}
              >
                <Download size={19} />
                {t("download")}
              </a>
            </div>
          )}
        </form>
      </section>
    </main>
  );
}

function PublicThemeScene({
  codeProgress,
  theme,
  unlocked,
  verifying,
}: {
  codeProgress: number;
  theme: ShareTheme;
  unlocked: boolean;
  verifying: boolean;
}) {
  const progressClass = `is-code-${Math.min(codeProgress, 4)}`;
  const stateClass = `${unlocked ? "is-unlocked" : ""} ${
    verifying ? "is-verifying" : ""
  }`;

  if (theme === "cyber-transfer") {
    return (
      <div
        aria-hidden="true"
        className={`public-theme-scene cyber-transfer-scene ${progressClass} ${stateClass}`}
      >
        <div className="cyber-tunnel">
          <span className="cyber-lane one" />
          <span className="cyber-lane two" />
          <span className="cyber-lane three" />
          <span className="cyber-lane four" />
          <span className="cyber-lane five" />
          <span className="cyber-lane six" />
          <span className="cyber-packet one" />
          <span className="cyber-packet two" />
          <span className="cyber-packet three" />
          <span className="cyber-packet four" />
          <span className="cyber-packet five" />
        </div>
        <div className="cyber-portal">
          <span className="portal-ring outer" />
          <span className="portal-ring middle" />
          <span className="portal-ring inner" />
          <span className="portal-ring cross" />
          <span className="portal-core" />
          <span className="portal-scan one" />
          <span className="portal-scan two" />
        </div>
        <div className="cyber-auth">
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      className={`public-theme-scene cinematic-vault-scene ${progressClass} ${stateClass}`}
    >
      <div className="vault-door-depth">
        <span className="vault-depth-line top" />
        <span className="vault-depth-line bottom" />
        <span className="vault-depth-line left" />
        <span className="vault-depth-line right" />
      </div>
      <div className="vault-aperture">
        <span className="vault-plate one" />
        <span className="vault-plate two" />
        <span className="vault-plate three" />
        <span className="vault-plate four" />
        <span className="vault-ring outer" />
        <span className="vault-ring middle" />
        <span className="vault-ring inner" />
        <span className="vault-spoke one" />
        <span className="vault-spoke two" />
        <span className="vault-spoke three" />
        <span className="vault-spoke four" />
        <span className="vault-core-glow" />
      </div>
      <div className="vault-code-dial">
        <span />
        <span />
        <span />
        <span />
      </div>
      <span className="vault-release-beam" />
    </div>
  );
}

function errorKeyFor(error: unknown): TranslationKey {
  if (error instanceof ApiClientError) {
    if (error.code === "invalid_passkey") return "wrongPasskey";
    if (error.code === "share_expired") return "expired";
    if (error.code === "share_disabled") return "disabledShare";
    if (error.code === "too_many_attempts") return "tooManyAttempts";
  }
  return "unavailable";
}
